// src/pages/Home.tsx
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config"; // Adjust the import path as needed
import ItemTile from "../components/ItemTile"; // Import your ItemTile component
import { Box, Typography, Grid, Tab, Tabs } from "@mui/material";
import { Variant } from "../store/cartSlice"; // Import Variant type from cartSlice
import { RootState } from "../store/store";
import BigSaleBanner from "../components/BigSaleBanner";
import HeroCarousel from "./HeroCarousel";

interface Item {
  id: string;
  name: string;
  type: string;
  images?: string[];
  isActive: boolean;
  isSaleActive: boolean;
  sizeGuideUrl?: string;
  variants: Variant[];
}

const Home: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const userId = useSelector((state: RootState) => state.user.userId);
  const searchQuery = useSelector((state: RootState) => state.search.searchQuery); // Access searchQuery from Redux store
  const [showActiveOnly, setShowActiveOnly] = useState(true); // Add state to toggle active items

  // Reference for the category section
  const categorySectionRef = useRef<HTMLDivElement>(null);

  // Smooth Scroll Handler Function
  const scrollToCategories = () => {
    if (categorySectionRef.current) {
      // Offset values can be adjusted slightly if your sticky top header clips the tab bar
      const offset = 140; 
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = categorySectionRef.current.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const fetchItems = async (userId: string) => {
    const querySnapshot = await getDocs(collection(db, "items")); // Change "items" to your Firestore collection name

    const itemsArray: Item[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const isSaleActive = data.is_sale_active; // Use the boolean field to check sale status
      const variants = (data.variant || []).map((variant: any) => ({
        id: `${doc.id}-${variant.color}`,
        color: variant.color,
        colorCode: variant.color_code,
        design: variant.design,
        imageUrl: variant.image_url,
        material: variant.material,
        size: variant.size,
        description: variant.description,
        unitPrice: parseFloat(variant.unit_price),
        salePrice: parseFloat(variant.sale_price),
        availableQuantity: parseInt(variant.quantity, 10),
        quantity: parseInt(variant.quantity, 10), // Add quantity field, initialized to 0
      }))
      .filter((variant: Variant) => variant.availableQuantity > 0);

      if (variants.length === 0) return;

      itemsArray.push({
        id: doc.id,
        name: data.name || "Unnamed Item",
        type: data.type,
        images: data.images,
        isActive: data.is_active,
        isSaleActive: data.is_sale_active,
        variants: variants, // Pass variants here
        sizeGuideUrl: data.size_guide_url,
      });
    });

    // Sort items by the total available quantity of their variants
    const sortedItems = itemsArray.sort((a, b) => {
      const totalQuantityA = a.variants.reduce(
        (sum, variant) => sum + variant.availableQuantity,
        0
      );
      const totalQuantityB = b.variants.reduce(
        (sum, variant) => sum + variant.availableQuantity,
        0
      );
      return totalQuantityB - totalQuantityA; // Descending order
    });

    setItems(sortedItems); // Set sorted items to local state
  };

  useEffect(() => {
    if (userId) {
      fetchItems(userId);
    }
  }, [userId]);

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
  };

// Advanced Filtering logic supporting both active search query AND category selection
  const filteredItems = items.filter((item) => {
    const matchesActive = item.isActive === showActiveOnly;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Check if item's 'type' matches category or matches 'all'
    let matchesCategory = true;
    if (selectedCategory !== "all") {
      if (selectedCategory === "diapers") {
        matchesCategory = item.type.includes("diaper");
      } else {
        matchesCategory = item.type === selectedCategory;
      }
    }

    return matchesActive && matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{ width: "100%", overflowX: "hidden"}}>

      {/* 1. HERO BANNER CAROUSEL (Passing the scroll function down) */}
      <HeroCarousel onScrollClick={scrollToCategories} />
      
      {/* Main Page Content Wrapper */}
      <Box sx={{ padding: { md: "0 50px 50px 50px", xs: "0 20px 20px 20px" } }}>
      {/* Modern, Aesthetic Category Filter Tabs */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4, borderBottom: "1px solid #e0e0e0" }}>
        <Tabs 
          value={selectedCategory} 
          onChange={handleCategoryChange} 
          variant="scrollable"
          scrollButtons="auto"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: "#57b7fb",
              height: "3px"
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 500,
              color: "#888",
              px: { xs: 2, sm: 3 },
              "&.Mui-selected": {
                color: "#57b7fb"
              }
            }
          }}
        >
          <Tab value="all" label="All Collections" />
          <Tab value="newborn" label="Newborn Essentials" />
          <Tab value="girl" label="Girl" />
          <Tab value="boy" label="Boy" />
          <Tab value="unisex" label="Unisex" />
          <Tab value="diaper" label="Cloth Diapers" />
          <Tab value="home-wear" label="Home Wear" />
        </Tabs>
      </Box>
      
      {/* Product Grid */}
      {filteredItems.length > 0 ? (
        <Grid container spacing={2} justifyContent="center" paddingTop="10px" alignItems="center">
          {filteredItems.map((item) => (
            <Grid item key={item.id} xs={12} sm={6} md={3} display="flex" justifyContent="center">
              <ItemTile item={item} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="body1" color="textSecondary">
            No snuggly items found in this category. More launching soon!
          </Typography>
        </Box>
      )}
    </Box>
    </Box>
  );
};

export default Home;
